// Draws one carousel slide on a 1080 x 1350 canvas.
//
// Used by the editor, the preview window and the PDF export, so all three
// always look the same. The caller resolves the palette and font pair first
// (see palettes.js and fonts.js) and waits for loadCarouselFonts() before
// drawing.
//
// Slides come from heuristicPlan:
//   { type: "hook", title, subtitle }
//   { type: "content", title, body }
//   { type: "outro", title, hashtags }
//
// Color is applied by role (see buildTheme): the background covers most of
// the slide, the accent only marks a few things (keyword, badges, progress,
// pills, shapes).

import { CANVAS_W, CANVAS_H, PAD, pad2, wrapText, roundRect } from "./canvas";
import { buildTheme, contrastRatio, withAlpha } from "./palettes";
import { drawBackground } from "./backgrounds";
import { DEFAULT_SIZES, resolveFontPair } from "./fonts";

// invertEnds: the first and last slide are flooded with the accent color.
// h1Scale / h2Scale / ctaScale: how much bigger or smaller this layout draws
// the hook, content and last slide titles than the chosen sizes (Clean = 1).
// highlight: how the keyword in a title stands out
// ("marker" = filled box behind it, "color" = accent-colored word).
export const LAYOUTS = {
  clean: {
    name: "Clean",
    invertEnds: false,
    h1Scale: 1,
    h2Scale: 1,
    ctaScale: 1,
    titleWeight: 700,
    highlight: "color",
  },
  bold: {
    name: "Bold",
    invertEnds: true,
    h1Scale: 1.12,
    h2Scale: 1.23,
    ctaScale: 1.12,
    titleWeight: 800,
    highlight: "marker",
  },
  editorial: {
    name: "Editorial",
    invertEnds: false,
    h1Scale: 0.94,
    h2Scale: 1.07,
    ctaScale: 0.94,
    titleWeight: 600,
    highlight: "color",
  },
};

// Order shown in the Layout panel. The labels are translated in the UI.
export const LAYOUT_IDS = ["clean", "bold", "editorial"];

export const DEFAULT_LAYOUT_ID = "clean";
export const DEFAULT_LINE_WEIGHT = 3;
export const LINE_WEIGHT_RANGE = { min: 1, max: 5 };

function cleanWord(w) {
  return String(w || "").replace(/[^\p{L}\p{N}%$]/gu, "");
}

// The keyword that gets the accent: a number or year if there is one,
// else the longest word.
function pickHighlightWord(title) {
  const words = String(title || "").split(/\s+/).filter(Boolean);
  const withNumber = words.find((w) => /\d{2,}/.test(w));
  if (withNumber) return cleanWord(withNumber);
  const longest = [...words].sort(
    (x, y) => cleanWord(y).length - cleanWord(x).length
  )[0];
  return longest ? cleanWord(longest) : "";
}

// Draws wrapped title lines word by word so one keyword can be emphasized.
// The caller sets ctx.font first. Returns the baseline of the NEXT line.
function drawTitle(ctx, lines, opts) {
  const { x, y, size, lineHeight, theme, highlightWord, mode } = opts;
  const space = ctx.measureText(" ").width;
  // If accent and text are nearly the same color (for example Light), a
  // colored word would not stand out, so it falls back to a filled marker.
  const lowContrast = contrastRatio(theme.accent, theme.text) < 1.6;
  const effectiveMode = lowContrast ? "marker" : mode;
  let cy = y;
  lines.forEach((line) => {
    let cx = x;
    line.split(" ").forEach((word) => {
      const w = ctx.measureText(word).width;
      const hit = highlightWord && cleanWord(word) === highlightWord;
      const marker = hit && effectiveMode === "marker";
      // The marker box is wider than the word: keep it off its neighbours
      const pad = marker ? Math.round(size * 0.18) : 0;
      if (marker) {
        cx += pad;
        ctx.fillStyle = theme.accent;
        roundRect(
          ctx,
          cx - pad,
          cy - size * 0.84,
          w + pad * 2,
          size * 1.06,
          Math.round(size * 0.18)
        );
        ctx.fill();
        ctx.fillStyle = theme.onAccent;
      } else if (hit) {
        ctx.fillStyle = theme.accent;
      } else {
        ctx.fillStyle = theme.text;
      }
      ctx.fillText(word, cx, cy);
      cx += w + pad + space;
    });
    cy += lineHeight;
  });
  return cy;
}

const hashtagLabel = (tag) => {
  const s = String(tag);
  return s.startsWith("#") ? s : `#${s}`;
};

// Pill measurements for a hashtag text size (28px gives a 56px tall pill).
const pillBox = (size) => {
  const h = Math.round(size * 2);
  return { h, padX: Math.round(size * 0.79), gap: 14, rowGap: h + 10 };
};

// How many rows the hashtag pills need.
function pillRows(ctx, tags, maxWidth, fonts, size) {
  const box = pillBox(size);
  ctx.font = `600 ${size}px ${fonts.body}`;
  let cx = 0;
  let rows = tags.length ? 1 : 0;
  tags.forEach((tag) => {
    const textW = ctx.measureText(hashtagLabel(tag)).width;
    const w = textW + box.padX * 2;
    if (cx + w > maxWidth && cx > 0) {
      cx = 0;
      rows += 1;
    }
    cx += w + box.gap;
  });
  return rows;
}

function drawPills(ctx, tags, opts) {
  const { x, y, maxWidth, theme, fonts, outline, size } = opts;
  const box = pillBox(size);
  ctx.font = `600 ${size}px ${fonts.body}`;
  let cx = x;
  let cy = y;
  tags.forEach((tag) => {
    const label = hashtagLabel(tag);
    const w = ctx.measureText(label).width + box.padX * 2;
    if (cx + w > x + maxWidth && cx > x) {
      cx = x;
      cy += box.rowGap;
    }
    if (outline) {
      ctx.strokeStyle = theme.accent;
      ctx.lineWidth = 3;
      roundRect(ctx, cx, cy, w, box.h, box.h / 2);
      ctx.stroke();
      ctx.fillStyle = theme.accent;
    } else {
      ctx.fillStyle = theme.accent;
      roundRect(ctx, cx, cy, w, box.h, box.h / 2);
      ctx.fill();
      ctx.fillStyle = theme.onAccent;
    }
    ctx.fillText(label, cx + box.padX, cy + box.h * 0.66);
    cx += w + box.gap;
  });
}

// Speech bubble in a rounded square (no image files needed): a solid bubble
// with its tail at the bottom-left and three dots cut out in the badge color.
// The bubble is drawn on a 24 x 24 grid at a fixed size; the square adds
// BADGE_PAD around it, in slide pixels.
const BADGE_ICON = 54; // bubble grid size in slide pixels (24 units)
const BADGE_PAD = 10;
const BADGE_SIZE = BADGE_ICON + BADGE_PAD * 2;

function drawChatBadge(ctx, x, y, bg, fg) {
  const size = BADGE_SIZE;
  roundRect(ctx, x, y, size, size, Math.round(size * 0.14));
  ctx.fillStyle = bg;
  ctx.fill();

  ctx.save();
  ctx.translate(x + size / 2, y + size / 2);
  ctx.scale(BADGE_ICON / 24, BADGE_ICON / 24);

  // Bubble body and tail
  ctx.fillStyle = fg;
  roundRect(ctx, -10, -9, 20, 15, 3);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-10, 3);
  ctx.lineTo(-10, 10);
  ctx.lineTo(-3.5, 5.5);
  ctx.closePath();
  ctx.fill();

  // Three dots
  ctx.fillStyle = bg;
  [-5, 0, 5].forEach((dx) => {
    ctx.beginPath();
    ctx.arc(dx, -1.5, 1.6, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

// Draws the lines of a body text block, one under the other.
function drawLines(ctx, lines, x, y, lineHeight) {
  let ly = y;
  lines.forEach((l) => {
    ctx.fillText(l, x, ly);
    ly += lineHeight;
  });
}

// palette: { bg, text, accent } (resolvePalette).
// fonts: a font pair (resolveFontPair).
// sizes: { h1, h2, cta, body, hashtags } (see DEFAULT_SIZES in fonts.js).
// lineWeight: 1 to 5, 3 = designed look (see DEFAULT_LINE_WEIGHT).
// logoRel: { xPct, yPct, wPct, opacity } relative to the slide.
// showLogoHandle: draws the resize handle on the logo
// (editor only, never in the export).
export function drawSlide(ctx, options) {
  const {
    slide,
    index = 0,
    total = 1,
    palette,
    fonts = resolveFontPair(),
    sizes,
    lineWeight = DEFAULT_LINE_WEIGHT,
    layoutId,
    bgStyle,
    bgOptions,
    bgBitmap = null,
    bgOverlay,
    logoBitmap = null,
    logoRel = null,
    showLogoHandle = false,
  } = options || {};

  if (!slide) return;
  const sz = { ...DEFAULT_SIZES, ...(sizes || {}) };
  const bodySize = sz.body;
  const lw = (Number(lineWeight) || DEFAULT_LINE_WEIGHT) / DEFAULT_LINE_WEIGHT;
  const layout = LAYOUTS[layoutId] ? layoutId : DEFAULT_LAYOUT_ID;
  const L = LAYOUTS[layout];
  const isEnd = slide.type !== "content";
  const inverted = L.invertEnds && isEnd && !bgBitmap;
  const theme = buildTheme(palette, inverted);
  const maxWidth = CANVAS_W - PAD * 2;
  const isLast = index === total - 1;
  const footerY = CANVAS_H - 84;
  const topY = PAD + 70; // below the header label
  const bottomY = footerY - 56; // above the progress bar
  const setSpacing = (v) => {
    if ("letterSpacing" in ctx) ctx.letterSpacing = v;
  };
  const titleFont = (size) => `${L.titleWeight} ${size}px ${fonts.title}`;
  const bodyFont = (weight, size) => `${weight} ${size}px ${fonts.body}`;
  const bodyW = fonts.bodyWeight || 400;
  const subW = fonts.subtitleWeight || 500;
  const highlightWord = pickHighlightWord(slide.title);
  const mode = L.highlight;

  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  drawBackground(ctx, {
    style: bgStyle,
    theme,
    index,
    bgBitmap,
    bgOverlay,
    options: bgOptions,
  });
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  setSpacing("0px");
  ctx.globalAlpha = 1;

  // Header label "01 / 07"
  // (Bold uses a giant number on content slides instead)
  if (!(layout === "bold" && slide.type === "content")) {
    ctx.font = bodyFont(600, 26);
    ctx.fillStyle = theme.muted;
    setSpacing("4px");
    ctx.fillText(`${pad2(index + 1)} / ${pad2(total)}`, PAD, PAD + 30);
    setSpacing("0px");
  }

  // Footer: progress segments + swipe arrow (not on the last slide)
  const segW = Math.min(90, (maxWidth * 0.6 - (total - 1) * 8) / total);
  for (let k = 0; k < total; k++) {
    const done = k <= index;
    ctx.fillStyle = done ? theme.accent : withAlpha(theme.text, 0.2);
    roundRect(
      ctx,
      PAD + k * (segW + 8),
      footerY - 4 * lw,
      segW,
      8 * lw,
      4 * lw
    );
    ctx.fill();
  }
  if (!isLast) {
    const ax = CANVAS_W - PAD - 30;
    ctx.fillStyle = theme.accent;
    ctx.beginPath();
    ctx.arc(ax, footerY, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = theme.onAccent;
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(ax - 12, footerY);
    ctx.lineTo(ax + 12, footerY);
    ctx.moveTo(ax, footerY - 12);
    ctx.lineTo(ax + 12, footerY);
    ctx.lineTo(ax, footerY + 12);
    ctx.stroke();
  }

  if (slide.type === "hook") {
    const size = Math.round(sz.h1 * L.h1Scale);
    const lh = size * 1.2;
    ctx.font = titleFont(size);
    const lines = wrapText(ctx, slide.title, maxWidth);
    ctx.font = bodyFont(subW, bodySize);
    const subLines = slide.subtitle
      ? wrapText(ctx, slide.subtitle, maxWidth)
      : [];
    const subLh = bodySize * 1.4;
    let decorTop = 0;
    if (layout === "bold") decorTop = 60;
    if (layout === "editorial") decorTop = 120;
    const decorBottom = layout === "clean" ? 56 : 0;
    const subH = subLines.length ? 34 + subLines.length * subLh : 0;
    const blockH = decorTop + lines.length * lh + decorBottom + subH;
    let y = topY + Math.max(0, (bottomY - topY - blockH) / 2);

    if (layout === "bold") {
      ctx.fillStyle = theme.accent;
      roundRect(ctx, PAD, y, 140, 16 * lw, 8 * lw);
      ctx.fill();
    }
    if (layout === "editorial") {
      // Large opening quote mark
      ctx.font = `600 230px ${fonts.title}`;
      ctx.fillStyle = theme.accent;
      ctx.fillText("\u201C", PAD - 8, y + 150);
    }
    y += decorTop;

    ctx.font = titleFont(size);
    y += size * 0.86;
    y = drawTitle(ctx, lines, {
      x: PAD,
      y,
      size,
      lineHeight: lh,
      theme,
      highlightWord,
      mode,
    });
    const lastBase = y - lh;

    if (layout === "clean") {
      ctx.fillStyle = theme.accent;
      roundRect(ctx, PAD, lastBase + 44, 140, 12 * lw, 6 * lw);
      ctx.fill();
    }
    if (subLines.length) {
      ctx.font = bodyFont(subW, bodySize);
      ctx.fillStyle = theme.muted;
      const sy = lastBase + decorBottom + 34 + bodySize * 0.9;
      drawLines(ctx, subLines, PAD, sy, subLh);
    }
  } else if (slide.type === "content") {
    const num = pad2(index);
    const isEditorial = layout === "editorial";
    const tSize = Math.round(sz.h2 * L.h2Scale);
    const tLh = tSize * 1.22;
    const bSize = Math.round(bodySize * (isEditorial ? 1.04 : 1));
    const bLh = bSize * (isEditorial ? 1.6 : 1.5);
    const avail = bottomY - topY;

    if (layout === "clean") {
      // No card: accent line on the left, big accent number, title,
      // short muted divider, body
      const barW = 10 * lw;
      const ruleH = 3 * lw;
      const innerX = PAD + barW + 40;
      const innerW = maxWidth - barW - 40;
      const numSize = 120;
      const numH = Math.round(numSize * 0.78); // cap height of the number
      ctx.font = titleFont(tSize);
      const tLines = wrapText(ctx, slide.title, innerW);
      ctx.font = bodyFont(bodyW, bSize);
      const bLines = wrapText(ctx, slide.body, innerW);
      const titleH = tLines.length ? 36 + tLines.length * tLh : 0;
      const bodyH = bLines.length ? 28 + ruleH + 30 + bLines.length * bLh : 0;
      const blockH = numH + titleH + bodyH;
      const y0 = topY + Math.max(0, (avail - blockH) / 2);

      ctx.fillStyle = theme.accent;
      roundRect(ctx, PAD, y0, barW, Math.max(barW, blockH), barW / 2);
      ctx.fill();

      ctx.font = `800 ${numSize}px ${fonts.title}`;
      ctx.fillStyle = theme.accent;
      ctx.fillText(num, innerX, y0 + numH);

      let lastBase = y0 + numH;
      if (tLines.length) {
        ctx.font = titleFont(tSize);
        const y = drawTitle(ctx, tLines, {
          x: innerX,
          y: y0 + numH + 36 + tSize * 0.86,
          size: tSize,
          lineHeight: tLh,
          theme,
          highlightWord,
          mode,
        });
        lastBase = y - tLh + tSize * 0.3;
      }

      if (bLines.length) {
        const ruleY = lastBase + 28;
        ctx.fillStyle = withAlpha(theme.text, 0.2);
        ctx.fillRect(innerX, ruleY, 80, ruleH);

        ctx.font = bodyFont(bodyW, bSize);
        ctx.fillStyle = withAlpha(theme.text, 0.88);
        const by = ruleY + ruleH + 30 + bSize * 0.9;
        drawLines(ctx, bLines, innerX, by, bLh);
      }
    } else if (layout === "bold") {
      // Giant faded number behind the text, thick accent bar on the left
      const innerX = PAD + 48;
      const innerW = maxWidth - 48;
      ctx.font = titleFont(tSize);
      const tLines = wrapText(ctx, slide.title, innerW);
      ctx.font = bodyFont(bodyW, bSize);
      const bLines = wrapText(ctx, slide.body, innerW);
      const blockH = tLines.length * tLh + 40 + bLines.length * bLh;
      const y0 = topY + Math.max(0, (avail - blockH) / 2);

      ctx.font = `800 440px ${fonts.title}`;
      ctx.textAlign = "right";
      ctx.fillStyle = withAlpha(theme.accent, 0.16);
      ctx.fillText(num, CANVAS_W - PAD + 24, topY + 330);
      ctx.textAlign = "left";

      ctx.fillStyle = theme.accent;
      const sideW = 14 * lw;
      roundRect(ctx, PAD, y0 + 6, sideW, Math.max(sideW, blockH - 12), 7 * lw);
      ctx.fill();

      ctx.font = titleFont(tSize);
      let y = y0 + tSize * 0.86;
      y = drawTitle(ctx, tLines, {
        x: innerX,
        y,
        size: tSize,
        lineHeight: tLh,
        theme,
        highlightWord,
        mode,
      });
      const lastBase = y - tLh;
      ctx.font = bodyFont(bodyW, bSize);
      ctx.fillStyle = withAlpha(theme.text, 0.88);
      const by = lastBase + tSize * 0.3 + 40 + bSize * 0.9;
      drawLines(ctx, bLines, innerX, by, bLh);
    } else {
      // Editorial: "— 01" label, title, short accent rule, body
      ctx.font = titleFont(tSize);
      const tLines = wrapText(ctx, slide.title, maxWidth);
      ctx.font = bodyFont(bodyW, bSize);
      const bLines = wrapText(ctx, slide.body, maxWidth);
      const blockH =
        28 + 40 + tLines.length * tLh + 30 + 4 * lw + 34 + bLines.length * bLh;
      const y0 = topY + Math.max(0, (avail - blockH) / 2);

      ctx.font = bodyFont(600, 28);
      ctx.fillStyle = theme.accent;
      setSpacing("6px");
      ctx.fillText(`\u2014 ${num}`, PAD, y0 + 28);
      setSpacing("0px");

      ctx.font = titleFont(tSize);
      let y = y0 + 28 + 40 + tSize * 0.86;
      y = drawTitle(ctx, tLines, {
        x: PAD,
        y,
        size: tSize,
        lineHeight: tLh,
        theme,
        highlightWord,
        mode,
      });
      const lastBase = y - tLh;
      const ruleY = lastBase + tSize * 0.3 + 30;
      ctx.fillStyle = theme.accent;
      roundRect(ctx, PAD, ruleY, 90, 4 * lw, 2 * lw);
      ctx.fill();

      ctx.font = bodyFont(bodyW, bSize);
      ctx.fillStyle = withAlpha(theme.text, 0.9);
      const by = ruleY + 4 * lw + 34 + bSize * 0.9;
      drawLines(ctx, bLines, PAD, by, bLh);
    }
  } else if (slide.type === "outro") {
    const size = Math.round(sz.cta * L.ctaScale);
    const lh = size * 1.22;
    ctx.font = titleFont(size);
    const lines = wrapText(ctx, slide.title, maxWidth);
    const tags = Array.isArray(slide.hashtags)
      ? slide.hashtags.filter(Boolean)
      : [];
    const tagSize = sz.hashtags;
    const box = pillBox(tagSize);
    const rows = pillRows(ctx, tags, maxWidth, fonts, tagSize);
    const pillsH = rows ? (rows - 1) * box.rowGap + box.h : 0;
    const iconH = layout === "editorial" ? 28 : BADGE_SIZE + 16;
    const blockH = iconH + lines.length * lh + (pillsH ? 44 + pillsH : 0);
    let y = topY + Math.max(0, (bottomY - topY - blockH) / 2);

    if (layout === "editorial") {
      ctx.fillStyle = theme.accent;
      roundRect(ctx, PAD, y, 90, 4 * lw, 2 * lw);
      ctx.fill();
    } else {
      drawChatBadge(ctx, PAD, y, theme.accent, theme.onAccent);
    }
    y += iconH;

    ctx.font = titleFont(size);
    y += size * 0.86;
    y = drawTitle(ctx, lines, {
      x: PAD,
      y,
      size,
      lineHeight: lh,
      theme,
      highlightWord,
      mode,
    });
    const lastBase = y - lh;
    if (pillsH) {
      drawPills(ctx, tags, {
        x: PAD,
        y: lastBase + size * 0.3 + 44,
        maxWidth,
        theme,
        fonts,
        outline: layout === "editorial",
        size: tagSize,
      });
    }
  }

  // Logo on top of everything
  const hasLogo =
    logoBitmap && logoRel && logoBitmap.width > 0 && logoBitmap.height > 0;
  if (hasLogo) {
    const lw = logoRel.wPct * CANVAS_W;
    const lh = lw / (logoBitmap.width / logoBitmap.height);
    const lx = logoRel.xPct * CANVAS_W;
    const ly = logoRel.yPct * CANVAS_H;
    ctx.globalAlpha = logoRel.opacity ?? 1;
    ctx.drawImage(logoBitmap, lx, ly, lw, lh);
    ctx.globalAlpha = 1;

    // Resize handle: editor only, never in the export
    if (showLogoHandle) {
      ctx.beginPath();
      ctx.arc(lx + lw, ly + lh, 18, 0, Math.PI * 2);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#111827";
      ctx.stroke();
    }
  }
}